/**
 * Carrier Cutoffs API Service
 * Carrier list comes live from Core DB (via the backend); cutoff config is fleet-owned.
 * All times are "HH:mm" strings in Asia/Baghdad.
 */

import api from './api';

export interface CarrierCutoffConfig {
  _id: string;
  carrier_id: number;
  carrier_uid: string;
  carrier_slug: string;
  carrier_title: string;
  returns_arrival_time?: string | null; // when the carrier delivers returned shipments
  collection_start_time?: string | null; // earliest outgoing-mail collection
  cutoff_time: string; // FINAL outgoing-mail collection deadline
  notes?: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface MergedCarrierCutoff {
  carrier_id: number;
  carrier_uid: string;
  carrier_slug: string;
  carrier_title: string;
  /** True when this config's carrier no longer exists in Core */
  core_missing: boolean;
  cutoff: CarrierCutoffConfig | null;
}

export interface SetCarrierCutoffInput {
  returnsArrivalTime?: string | null;
  collectionStartTime?: string | null;
  cutoffTime: string;
  notes?: string;
  isActive?: boolean;
}

/**
 * Get the merged carrier list (all Core carriers + their cutoff config or null)
 */
export async function getCarrierCutoffs(includeInactive = false): Promise<MergedCarrierCutoff[]> {
  const params = new URLSearchParams();
  if (includeInactive) params.append('includeInactive', 'true');

  const response = await api.get<{ success: boolean; data: MergedCarrierCutoff[] }>(
    `/carrier-cutoffs?${params.toString()}`
  );
  return response.data.data;
}

/**
 * Create-or-update the cutoff config for a carrier
 */
export async function setCarrierCutoff(
  carrierId: number,
  input: SetCarrierCutoffInput
): Promise<CarrierCutoffConfig> {
  const response = await api.put<{ success: boolean; data: CarrierCutoffConfig }>(
    `/carrier-cutoffs/carrier/${carrierId}`,
    input
  );
  return response.data.data;
}

/**
 * Delete a carrier's cutoff config (soft by default)
 */
export async function deleteCarrierCutoff(carrierId: number, hard = false): Promise<void> {
  await api.delete(`/carrier-cutoffs/carrier/${carrierId}${hard ? '?hard=true' : ''}`);
}
